import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Dropdown,
  Checkbox,
  Input,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_LAWYER_STOCK,
  POST_LOAN_DB2_V2,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";
import { formatDate } from "../contractDetail/contractDetailNew/utils";

const TwoLineHeader = ({ top, bottom }) => (
  <div style={{ textAlign: "center", lineHeight: 1.4 }}>
    <div>{top}</div>
    <div style={{ fontSize: 11, fontWeight: 400, color: "#888" }}>{bottom}</div>
  </div>
);
// คำนวณยอดรวม ใช้ร่วมกับ table summary / excel / pdf
const formatTypeModelColor = (r) =>
  [r.TYPE, r.MODEL, r.COLOR].filter(Boolean).join(" - ");

// ราคาต้นทุน / ราคาขาย ของ stock ใช้ร่วมกับ column / summary / excel / pdf
const getStockCost = (r) => Number(r.credit_debtor) || 0;
const getStockSale = (r) => Number(r.credit_debtor) || 0;

const calcStockTotals = (data) =>
  data.reduce(
    (acc, r) => ({
      cost: acc.cost + getStockCost(r),
      sale: acc.sale + getStockSale(r),
    }),
    { cost: 0, sale: 0 },
  );

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [date, setDate] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [isFlagAgreement, setIsFlagAgreement] = useState(true);
  const [isFlagEnforce, setIsFlagEnforce] = useState(true);
  const [searchContno, setSearchContno] = useState("");
  // filter ข้อมูลในตาราง ตามวันที่รับ (dt_bel)
  const [filterDate, setFilterDate] = useState([null, null]);

  // ── Query ─────────────────────────────────────────────
  const onQuery = () => {
    if (!date?.[1]) {
      message.warning("กรุณาเลือกวันที่สิ้นสุดก่อนค้นหา");
      return;
    }
    queryData({
      firstDate: date[0] ? dayjs(date[0]).format("YYYY-MM-DD") : null,
      lastDate: dayjs(date[1]).format("YYYY-MM-DD"),
    });
  };

  const queryData = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_LAWYER_STOCK, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });
      if (res.status !== 200) {
        setArrayPay([]);
        return;
      }

      const reportData = res.data?.data ?? [];

      if (!reportData.length) {
        setArrayPay([]);
        return;
      }

      const groupedBySchema = reportData.reduce((acc, item) => {
        if (!item.CONTNO || !item.SCHEMA) return acc;
        if (!acc[item.SCHEMA]) acc[item.SCHEMA] = [];
        acc[item.SCHEMA].push(`'${item.CONTNO}'`);
        return acc;
      }, {});

      const schemaPayload = Object.entries(groupedBySchema).reduce(
        (acc, [schema, arr]) => {
          acc[schema] = arr.join(",");
          return acc;
        },
        {},
      );

      let db2Map = {};
      try {
        const db2Res = await axios.post(POST_LOAN_DB2_V2, schemaPayload, {
          headers: HEADERS_EXPORT_BEN,
        });
        const db2Data = Array.isArray(db2Res.data)
          ? db2Res.data
          : Array.isArray(db2Res.data?.data)
            ? db2Res.data.data
            : [];

        db2Data.forEach((item) => {
          const contno = item?.LOAN?.CONTNO;
          if (!contno) return;
          db2Map[contno] = {
            LOCAT: item.LOAN?.LOCAT,
            TOTPRC: item.LOAN?.TOTPRC,
            NCSHPRC: item.LOAN?.NCSHPRC,
            SMPAY: item.LOAN?.SMPAY,
            PRODUCTLOAN: item.LOAN?.PRODUCTLOAN,
            ISSUNO: item.LOAN?.ISSUNO,
            TYPE: item.MORTGAGE?.TYPE,
            MODEL: item.MORTGAGE?.MODEL,
            BAAB: item.MORTGAGE?.BAAB,
            STRNO: item.MORTGAGE?.STRNO ?? item.LOAN?.STRNO,
            COLOR: item.MORTGAGE?.COLOR,
            REGNO: item.MORTGAGE?.REGNO,
            ENGNO: item.MORTGAGE?.ENGNO,
            MANUYR: item.MORTGAGE?.MANUYR,
            DORECV: item.MORTGAGE?.DORECV,
            GCODE: item.MORTGAGE?.GCODE,
            OFFIC: item.CUSTOMER?.OFFIC,
            OCCUP: item.CUSTOMER?.OCCUP,
            BIRTHDT: item.CUSTOMER?.BIRTHDT,
            ADDRESS: item.CUSTOMER?.ADDRESS ?? [],
            GUARANTORS: item.GUARANTORS ?? [],
          };
        });
      } catch (e) {
        console.warn("DB2 failed:", e);
      }
      setArrayPay(
        reportData.map((item) => ({ ...item, ...(db2Map[item.CONTNO] ?? {}) })),
      );
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    const keyword = searchContno.trim().toLowerCase();

    // เรียงตาม SDATE (ไม่มีวันที่ไว้ท้ายสุด)
    const sdateValue = (r) => {
      const d = dayjs(r.SDATE);
      return r.SDATE && d.isValid() ? d.valueOf() : Infinity;
    };

    return arrayPay
      .filter((item) => {
        // ✅ ตัด closeContno = 1 ออกเสมอ
        if (item.closeContno === 1) return false;

        // ✅ filter เลขสัญญา / เลขตัวถัง
        if (
          keyword &&
          ![item.new_contno, item.CONTNO, item.CONTNO_DB2, item.STRNO].some(
            (c) =>
              String(c ?? "")
                .toLowerCase()
                .includes(keyword),
          )
        )
          return false;

        // ✅ filter วันที่รับ
        if (filterDate[0] || filterDate[1]) {
          if (!item.dt_bel) return false;
          const d = dayjs(item.dt_bel);
          if (filterDate[0] && d.isBefore(dayjs(filterDate[0]).startOf("day")))
            return false;
          if (filterDate[1] && d.isAfter(dayjs(filterDate[1]).endOf("day")))
            return false;
        }

        // ✅ ทั้งคู่ true → เอาทั้งหมด
        if (isFlagAgreement && isFlagEnforce) return true;

        // ✅ Agreement อย่างเดียว → flagLoanNew = 1
        if (isFlagAgreement && !isFlagEnforce) return item.flagLoanNew === 1;

        // ✅ Enforce อย่างเดียว → flagLoanNew = 2
        if (!isFlagAgreement && isFlagEnforce) return item.flagLoanNew === 2;

        // ✅ ทั้งคู่ false → ไม่มีข้อมูล
        return false;
      })
      .sort((a, b) => {
        const av = sdateValue(a);
        const bv = sdateValue(b);
        return av === bv ? 0 : av < bv ? -1 : 1;
      });
  }, [arrayPay, isFlagAgreement, isFlagEnforce, searchContno, filterDate]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "สาขา",
      align: "center",
      width: 60,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.LOCAT || "-"}</div>
        </>
      ),
    },
    {
      title: "เลขที่ใบรับ",
      align: "center",
      width: 60,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.RECVNO || "-"}</div>
        </>
      ),
    },
    {
      title: "วันที่รับ",
      key: "dt_bel",
      align: "center",
      width: 130,
      render: (_, r) => <div>{formatDate(r.dt_bel) || "-"}</div>,
    },
    {
      title: "เลขสัญญา",
      key: "CONTNO_DB2",
      align: "center",
      width: 130,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.CONTNO_DB2 || "-"}</div>
        </>
      ),
    },
    {
      title: "ยี่ห้อ-รุ่น-สี",
      key: "type_model_color",
      align: "left",
      width: 180,
      render: (_, r) => <div>{formatTypeModelColor(r) || "-"}</div>,
    },

    {
      title: "เลขตัวถัง",
      key: "STRNO",
      align: "center",
      width: 150,
      render: (_, r) => <div>{r.STRNO || "-"}</div>,
    },
    {
      title: "เลขเครื่อง",
      key: "ENGNO",
      align: "center",
      width: 130,
      render: (_, r) => <div>{r.ENGNO || "-"}</div>,
    },
    {
      title: "ทะเบียน",
      key: "REGNO",
      align: "center",
      width: 100,
      render: (_, r) => <div>{r.REGNO || "-"}</div>,
    },
    {
      title: "ปีผลิต",
      key: "MANUYR",
      align: "center",
      width: 80,
      render: (_, r) => <div>{r.MANUYR || "-"}</div>,
    },
    {
      title: "ราคาต้นทุน",
      key: "stock_cost",
      align: "right",
      width: 100,
      render: (_, r) => <div>{currencyFormatPoint(getStockCost(r))}</div>,
    },
    {
      title: "ราคาขาย",
      key: "stock_sale",
      align: "right",
      width: 100,
      render: (_, r) => <div>{currencyFormatPoint(getStockSale(r))}</div>,
    },
    {
      title: "กลุ่ม",
      key: "GCODE",
      align: "center",
      width: 80,
      render: (_, r) => <div>{r.GCODE || "-"}</div>,
    },
  ];

  // ── Export Excel ──────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const fromDate = date[0] ? dayjs(date[0]).format("DD/MM/YYYY") : "";
    const toDate = date[1] ? dayjs(date[1]).format("DD/MM/YYYY") : "";

    const rows = filteredData.map((r, i) => ({
      ลำดับ: i + 1,
      สาขา: r.LOCAT || "",
      เลขที่ใบรับ: r.RECVNO || "",
      วันที่รับ: formatDate(r.dt_bel) || "",
      เลขสัญญา: r.CONTNO_DB2 || "",
      "ยี่ห้อ-รุ่น-สี": formatTypeModelColor(r),
      เลขตัวถัง: r.STRNO || "",
      เลขเครื่อง: r.ENGNO || "",
      ทะเบียน: r.REGNO || "",
      ปีผลิต: r.MANUYR || "",
      ราคาต้นทุน: getStockCost(r),
      ราคาขาย: getStockSale(r),
      กลุ่ม: r.GCODE || "",
    }));

    const moneyKeys = ["ราคาต้นทุน", "ราคาขาย"];
    const t = calcStockTotals(filteredData);

    const summaryRow = {
      ลำดับ: "",
      สาขา: `รวมทั้งหมด ${filteredData.length} รายการ`,
      เลขที่ใบรับ: "",
      วันที่รับ: "",
      เลขสัญญา: "",
      "ยี่ห้อ-รุ่น-สี": "",
      เลขตัวถัง: "",
      เลขเครื่อง: "",
      ทะเบียน: "",
      ปีผลิต: "",
      ราคาต้นทุน: t.cost,
      ราคาขาย: t.sale,
      กลุ่ม: "",
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);
    const keys = Object.keys(rows[0]);

    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    const getAlignment = (key) => {
      if (key === "ยี่ห้อ-รุ่น-สี") return "left";
      if (moneyKeys.includes(key)) return "right";
      return "center";
    };

    // header
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows
    rows.forEach((_, ri) => {
      const fill = {
        patternType: "solid",
        fgColor: { rgb: ri % 2 === 0 ? "EBF5FF" : "FFFFFF" },
      };
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        const isMoney = moneyKeys.includes(key);
        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: { horizontal: getAlignment(key), vertical: "center" },
          border: borderStyle,
          numFmt: isMoney ? "#,##0.00" : undefined,
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;
      const isMoney = moneyKeys.includes(key);
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "D5E8D4" } },
        font: { bold: true, sz: 12 },
        alignment: { horizontal: getAlignment(key), vertical: "center" },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2.5, 16) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, `รายงานรับเข้าstock_${fromDate}_${toDate}.xlsx`);
    message.success("Export Excel สำเร็จ");
  };

  // ── Export PDF ────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const fromDate = date[0] ? dayjs(date[0]).format("DD/MM/YYYY") : "";
    const toDate = date[1] ? dayjs(date[1]).format("DD/MM/YYYY") : "";

    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานรับเข้า stock", pageWidth / 2, 8, { align: "center" });
      doc.text(
        `ขายจากวันที่  ${fromDate}   ถึงวันที่  ${toDate}`,
        pageWidth / 2,
        14,
        { align: "center" },
      );
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(14);
      doc.text("บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)", 8, 20);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 26);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 26, { align: "right" });
    };

    const tableBody = filteredData.map((r, i) => [
      i + 1,
      r.LOCAT || "-",
      r.RECVNO || "-",
      formatDate(r.dt_bel) || "-",
      r.CONTNO_DB2 || "-",
      formatTypeModelColor(r) || "-",
      r.STRNO || "-",
      r.ENGNO || "-",
      r.REGNO || "-",
      r.MANUYR || "-",
      currencyFormatPoint(getStockCost(r)),
      currencyFormatPoint(getStockSale(r)),
      r.GCODE || "-",
    ]);

    const t = calcStockTotals(filteredData);
    const summaryStyle = {
      fontStyle: "bold",
      font: "THSarabunNew",
      fillColor: [236, 240, 241],
    };

    const summaryRow = [
      {
        content: `รวมทั้งหมด ${filteredData.length} รายการ`,
        colSpan: 10,
        styles: { ...summaryStyle, halign: "center" },
      },
      {
        content: currencyFormatPoint(t.cost),
        styles: { ...summaryStyle, halign: "right" },
      },
      {
        content: currencyFormatPoint(t.sale),
        styles: { ...summaryStyle, halign: "right" },
      },
      { content: "", styles: summaryStyle },
    ];

    doc.autoTable({
      startY: 30,
      head: [
        [
          "ลำดับ",
          "สาขา",
          "เลขที่ใบรับ",
          "วันที่รับ",
          "เลขสัญญา",
          "ยี่ห้อ-รุ่น-สี",
          "เลขตัวถัง",
          "เลขเครื่อง",
          "ทะเบียน",
          "ปีผลิต",
          "ราคาต้นทุน",
          "ราคาขาย",
          "กลุ่ม",
        ],
      ],
      body: [...tableBody, summaryRow],
      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 13,
      },
      columnStyles: {
        5: { halign: "left" }, // ยี่ห้อ-รุ่น-สี
        10: { halign: "right" }, // ราคาต้นทุน
        11: { halign: "right" }, // ราคาขาย
      },
      tableWidth: "auto",
      margin: { top: 30, left: 3, right: 3 },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      styles: {
        font: "THSarabunNew",
        fontSize: 12,
        cellPadding: 1,
        halign: "center",
        fillColor: [255, 255, 255],
      },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
    });

    doc.save(`รายงานรับเข้าstock_${fromDate}_${toDate}.pdf`);
  };

  // ✅ Dropdown menu เลือก PDF หรือ Excel
  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const hasDate = !!date[1];
  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);
  console.log("filteredData", filteredData);
  console.log("filteredData", filteredData);

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={16}>
            <Space style={{ marginBottom: 10 }}>
              เริ่ม
              <ThaiDatePicker
                size="large"
                value={date[0]}
                onChange={(value) => setDate([value, date[1]])}
                placeholder={"วันที่เริ่มต้น"}
                allowClear={true}
              />
              ถึง
              <ThaiDatePicker
                size="large"
                value={date[1]}
                onChange={(value) => setDate([date[0], value])}
                placeholder={"วันที่สิ้นสุด"}
                allowClear={false}
              />
              <Button size="large" type="primary" onClick={onQuery}>
                ค้นหา
              </Button>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: "end" }}>
            <Space style={{ marginBottom: 10 }}>
              {/* ✅ Dropdown เลือก PDF / Excel */}
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานรับเข้า stock</Divider>

          <Typography.Text
            strong
            style={{ display: "block", textAlign: "center", fontSize: 16 }}
          >
            บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)
          </Typography.Text>

          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              {date[0] &&
                `ขายจากวันที่ ${dayjs(date[0]).format("DD/MM/YYYY")} `}
              ถึงวันที่ {dayjs(date[1]).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          {/* ── Filter ข้อมูลในตาราง ── */}
          <Space wrap style={{ marginTop: 10 }}>
            วันที่รับ
            <ThaiDatePicker
              value={filterDate[0]}
              onChange={(value) => setFilterDate([value, filterDate[1]])}
              placeholder={"วันที่เริ่มต้น"}
              allowClear
              size="large"
            />
            ถึง
            <ThaiDatePicker
              value={filterDate[1]}
              onChange={(value) => setFilterDate([filterDate[0], value])}
              placeholder={"วันที่สิ้นสุด"}
              allowClear
              size="large"
            />
            <Input
              allowClear
              placeholder="ค้นหาเลขสัญญา / เลขตัวถัง"
              value={searchContno}
              onChange={(e) => setSearchContno(e.target.value)}
              style={{ width: 200 }}
              size="small"
            />
            <Button
              onClick={() => {
                setFilterDate([null, null]);
                setSearchContno("");
              }}
            >
              ล้างตัวกรอง
            </Button>
          </Space>

          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r.idJudge ?? r.CONTNO}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              const t = calcStockTotals(filteredData);
              const cs = { fontWeight: "bold", background: "#ecf0f1" };
              const cr = {
                fontWeight: "bold",
                background: "#ecf0f1",
                textAlign: "right",
              };

              return (
                <Table.Summary.Row>
                  {/* ลำดับ ... ปีผลิต → 10 ช่อง */}
                  <Table.Summary.Cell
                    index={0}
                    colSpan={10}
                    align="center"
                    style={cs}
                  >
                    รวมทั้งหมด {filteredData.length} รายการ
                  </Table.Summary.Cell>

                  {/* ราคาต้นทุน */}
                  <Table.Summary.Cell index={10} align="right" style={cr}>
                    {currencyFormatPoint(t.cost)}
                  </Table.Summary.Cell>

                  {/* ราคาขาย */}
                  <Table.Summary.Cell index={11} align="right" style={cr}>
                    {currencyFormatPoint(t.sale)}
                  </Table.Summary.Cell>

                  {/* กลุ่ม */}
                  <Table.Summary.Cell index={12} style={cs} />
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainReportIncomeStock = MotionHoc(Main);
export default MainReportIncomeStock;
